import React, { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { getAdminDocuments, updateDocumentLink, addDocument, deleteDocument, getDocumentDownloadUrl } from '../api';
import './ManageDocuments.css';

function ManageDocuments({ user, onLogout }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingDoc, setEditingDoc] = useState(null);
  const [linkInput, setLinkInput] = useState('');

  // Add document modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', google_docs_link: '', file_type: 'word' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const fileInputRef = useRef(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await getAdminDocuments();
      setDocuments(response.documents || []);
    } catch (error) {
      showMessage('error', error.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setLinkInput(doc.google_docs_link || '');
  };

  const handleCancelEdit = () => {
    setEditingDoc(null);
    setLinkInput('');
  };

  const handleSaveLink = async (docId) => {
    try {
      await updateDocumentLink(docId, linkInput);
      showMessage('success', 'Google Docs link updated successfully!');
      loadDocuments();
      setEditingDoc(null);
      setLinkInput('');
    } catch (error) {
      showMessage('error', error.message || 'Failed to update link');
    }
  };

  const handleDownload = (filename) => {
    const url = getDocumentDownloadUrl(filename);
    window.open(url, '_blank');
  };

  // === Add Document Handlers ===
  const handleOpenAddModal = () => {
    setAddForm({ title: '', google_docs_link: '', file_type: 'word' });
    setSelectedFile(null);
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddForm({ title: '', google_docs_link: '', file_type: 'word' });
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
      if (!allowed.includes(ext)) {
        showMessage('error', 'Invalid file type. Allowed: PDF, DOC, DOCX, XLS, XLSX');
        e.target.value = '';
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        showMessage('error', 'File size exceeds 20MB limit');
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleAddDocument = async () => {
    if (!addForm.title.trim()) {
      showMessage('error', 'Document title is required');
      return;
    }
    if (!selectedFile) {
      showMessage('error', 'Please select a file to upload');
      return;
    }

    setAdding(true);
    try {
      const formData = new FormData();
      formData.append('title', addForm.title.trim());
      formData.append('pdf_file', selectedFile);
      formData.append('google_docs_link', addForm.google_docs_link.trim());
      formData.append('file_type', addForm.file_type);

      await addDocument(formData);
      showMessage('success', 'Document added successfully!');
      handleCloseAddModal();
      loadDocuments();
    } catch (error) {
      showMessage('error', error.message || 'Failed to add document');
    } finally {
      setAdding(false);
    }
  };

  // === Delete Document Handlers ===
  const handleDeleteClick = (doc) => {
    setDeleteTarget(doc);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await deleteDocument(deleteTarget.id);
      showMessage('success', `Document "${deleteTarget.title}" deleted successfully!`);
      setDeleteTarget(null);
      loadDocuments();
    } catch (error) {
      showMessage('error', error.message || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="manage-documents-page">
        <div className="page-header">
          <div className="page-header-content">
            <svg className="page-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <line x1="10" y1="9" x2="8" y2="9"/>
            </svg>
            <div>
              <h1 className="page-title">Manage Documents</h1>
              <p className="page-subtitle">Add, edit, or remove document templates</p>
            </div>
          </div>
          <button className="btn btn-add" onClick={handleOpenAddModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Document
          </button>
        </div>

        {/* Success/Error Messages */}
        {message.text && (
          <div className={`message-banner ${message.type}`}>
            {message.type === 'success' ? (
              <svg className="message-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
            ) : (
              <svg className="message-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
              </svg>
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Documents List */}
        <div className="documents-section">
          <div className="section-header">
            <h2 className="section-title">Document Templates</h2>
            <span className="documents-count">{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading documents...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="empty-state">
              <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7"/>
                <path d="M3 7l2.4-4.8A1 1 0 0 1 6.3 1.5h11.4a1 1 0 0 1 .9.7L21 7"/>
                <line x1="9" y1="11" x2="9" y2="11"/>
                <line x1="15" y1="11" x2="15" y2="11"/>
              </svg>
              <p>No documents found</p>
              <p className="empty-hint">Click "Add Document" to upload your first template</p>
            </div>
          ) : (
            <div className="documents-table-wrapper">
              <table className="documents-table">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>PDF Reference</th>
                    <th>Google Docs Link</th>
                    <th>Type</th>
                    <th className="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="doc-name">
                        <div className="doc-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                        </div>
                        <span>{doc.title}</span>
                      </td>
                      <td>
                        <button
                          className="link-btn"
                          onClick={() => handleDownload(doc.pdf_filename)}
                          title="Download PDF reference"
                        >
                          {doc.pdf_filename}
                        </button>
                      </td>
                      <td className="google-docs-cell">
                        {editingDoc && editingDoc.id === doc.id ? (
                          <input
                            type="text"
                            className="link-input"
                            value={linkInput}
                            onChange={(e) => setLinkInput(e.target.value)}
                            placeholder="https://docs.google.com/..."
                          />
                        ) : (
                          <span className="link-display">
                            {doc.google_docs_link ? (
                              <a href={doc.google_docs_link} target="_blank" rel="noopener noreferrer">
                                {doc.google_docs_link.substring(0, 50)}...
                              </a>
                            ) : (
                              <span className="no-link">No link set</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${doc.file_type}`}>
                          {doc.file_type === 'excel' ? 'Sheets' : 'Docs'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        {editingDoc && editingDoc.id === doc.id ? (
                          <>
                            <button
                              className="action-btn save-btn"
                              onClick={() => handleSaveLink(doc.id)}
                              title="Save"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            </button>
                            <button
                              className="action-btn cancel-btn"
                              onClick={handleCancelEdit}
                              title="Cancel"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="action-btn edit-btn"
                              onClick={() => handleEdit(doc)}
                              title="Edit Link"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              className="action-btn delete-row-btn"
                              onClick={() => handleDeleteClick(doc)}
                              title="Delete Document"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add Document Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={handleCloseAddModal}>
            <div className="modal-content add-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">Add New Document</h2>

              <div className="form-group">
                <label className="form-label">Document Title <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Site Safety Daily Log"
                  value={addForm.title}
                  onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Upload File <span className="required">*</span></label>
                <div
                  className={`file-drop-area ${selectedFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {selectedFile ? (
                    <div className="selected-file-info">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <div>
                        <p className="selected-file-name">{selectedFile.name}</p>
                        <p className="selected-file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <svg className="upload-area-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <p className="file-drop-text">Click to select file</p>
                      <p className="file-drop-hint">PDF, DOC, DOCX, XLS, XLSX (max 20MB)</p>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Google Docs/Sheets Link <span className="optional">(optional)</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="https://docs.google.com/..."
                  value={addForm.google_docs_link}
                  onChange={(e) => setAddForm({ ...addForm, google_docs_link: e.target.value })}
                />
              </div>

              <div className="modal-actions">
                <button className="btn btn-cancel" onClick={handleCloseAddModal} disabled={adding}>
                  Cancel
                </button>
                <button className="btn btn-save" onClick={handleAddDocument} disabled={adding}>
                  {adding ? 'Uploading...' : 'Add Document'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteTarget && (
          <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">Delete Document</h2>
              <p className="modal-text">
                Are you sure you want to delete <strong>"{deleteTarget.title}"</strong>? This will remove the file and cannot be undone.
              </p>
              <div className="modal-actions">
                <button className="btn btn-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                  Cancel
                </button>
                <button className="btn btn-delete" onClick={handleConfirmDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ManageDocuments;
